import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTranslation } from "react-i18next";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any, row?: any) => string | React.ReactNode;
  sortable?: boolean;
}

interface EnhancedTableProps {
  columns: Column[];
  data: any[];
  title?: string;
  // titleMarginLeft?: number;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  searchFields?: string[];
  filterFields?: { field: string; options: string[] }[];
  onRowClick?: (row: any) => void;
  actions?: (row: any) => React.ReactNode;
  // External pagination props
  page?: number;
  rowsPerPage?: number;
  totalElements?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  enablePagination?: boolean;
  /** When true, show skeleton rows instead of data. */
  loading?: boolean;
  /** Optional override for how many skeleton rows to show. */
  skeletonRowCount?: number;
  // Styling props
  cardProps?: {
    elevation?: number;
    sx?: any;
  };
}

const EnhancedTable: React.FC<EnhancedTableProps> = ({
  columns,
  data,
  title,
  leftActions,
  rightActions,
  searchFields = [],
  filterFields = [],
  onRowClick,
  actions,
  // External pagination props
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  totalElements,
  onPageChange: externalOnPageChange,
  onRowsPerPageChange: externalOnRowsPerPageChange,
  enablePagination = false,
  loading = false,
  skeletonRowCount,
  // Styling props
  cardProps = {},
}) => {
  const { t } = useTranslation();
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<{ [key: string]: string }>({});

  // Use external pagination props if provided, otherwise use internal state
  const page = enablePagination && externalPage !== undefined ? externalPage : internalPage;
  const rowsPerPage = enablePagination && externalRowsPerPage !== undefined ? externalRowsPerPage : internalRowsPerPage;
  const effectiveSkeletonRows = skeletonRowCount ?? rowsPerPage;

  const handleChangePage = (event: unknown, newPage: number) => {
    if (enablePagination && externalOnPageChange) {
      externalOnPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (enablePagination && externalOnRowsPerPageChange) {
      externalOnRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalPage(0);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setInternalPage(0);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setInternalPage(0);
  };

  const filteredData = data.filter((row) => {
    // Column filters
    const matchesFilters = Object.entries(filters).every(
      ([field, value]) => value === "" || String(row[field]) === value
    );

    // Search filter
    if (searchFields.length === 0 || searchTerm === "") {
      return matchesFilters;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const isNumberSearch = /^\d+$/.test(searchLower);

    // If search is a number, only search by className field
    if (isNumberSearch && searchFields.includes('className')) {
      const classNameValue = String(row['className'] || '').toLowerCase().trim();
      // Match patterns like "1st", "2nd", "3rd", "10th", "11th", "12th", "1st A", "10th - A", etc.
      // The pattern matches: number followed by st/nd/rd/th (case-insensitive)
      const classPattern = new RegExp(`^${searchLower}(st|nd|rd|th)`, 'i');
      const matches = classPattern.test(classNameValue);
      return matches && matchesFilters;
    }

    // Default search behavior for non-number searches
    const matchesSearch = searchFields.some((field) => {
      const fieldValue = String(row[field] || '').toLowerCase();
      return fieldValue.includes(searchLower);
    });

    return matchesSearch && matchesFilters;
  });

  // Only apply internal pagination if external pagination is not enabled
  const paginatedData = enablePagination
    ? data // Use original data when external pagination is enabled
    : filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

  return (
    <Card
      elevation={cardProps.elevation !== undefined ? cardProps.elevation : 3}
      sx={{ width: "100%", mb: 3, ...cardProps.sx }}
    >
      <CardContent>
        {title && (
          <Typography variant="h6" gutterBottom component="div">
            {title}
          </Typography>
        )}

        <Box
          sx={{
            mb: 2,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: leftActions ? "space-between" : "flex-end",
            alignItems: "center",
          }}
        >
          {leftActions && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {leftActions}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {rightActions && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {rightActions}
              </Box>
            )}
          {searchFields.length > 0 && (
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
          )}

          {filterFields.map(({ field, options }) => (
            <FormControl key={field} size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t(field)}</InputLabel>
              <Select
                value={filters[field] || ""}
                label={t(field)}
                onChange={(e) => handleFilterChange(field, e.target.value)}
              >
                <MenuItem value="">{t("all")}</MenuItem>
                {options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "primary.light",
                    color: "white",
                    width: 75,
                  }}
                >
                  {t("s_no")}
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "primary.light",
                      color: "white",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "primary.light",
                      color: "white",
                    }}
                  >
                    {t("actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: effectiveSkeletonRows }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    <TableCell align="center">
                      <Skeleton variant="text" width={24} />
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell key={`${column.id}-s-${idx}`} align={column.align}>
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={22}
                          sx={{ mx: column.align === "right" ? "auto" : 0 }}
                        />
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell align="center">
                        <Skeleton variant="rounded" width={64} height={28} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (actions ? 1 : 0)}
                    align="center"
                  >
                    {t("no_data_found")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow
                    hover
                    key={index}
                    onClick={() => onRowClick && onRowClick(row)}
                    sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    <TableCell align="center">
                      {index + 1 + page * rowsPerPage}
                    </TableCell>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format ? column.format(value, row) : value}
                        </TableCell>
                      );
                    })}
                    {actions && (
                      <TableCell
                        align="center"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        {actions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={enablePagination && totalElements !== undefined ? totalElements : filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
};

export default EnhancedTable;
